import type { JSX } from 'react';

import { Redirect, useKnownAvailableParams } from '@change-corgi/core/react/router';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { withPrefetchedData } from 'src/shared/prefetch';
import type { PrefetchedDataProps } from 'src/shared/prefetch';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { MovementContextProvider } from '../hooks/provider';
import type { MovementInfoPrefetchedState } from '../hooks/useMovementInfo';
import { prefetchMovementInfo, useMovementInfo } from '../hooks/useMovementInfo';

import { MovementContainer } from './Movement';

type PrefetchedData = Readonly<{
	movementInfo: MovementInfoPrefetchedState;
}>;

export const MovementWrapperContainer = withPrefetchedData(
	function Movement({ prefetchedData }: PrefetchedDataProps<PrefetchedData>): JSX.Element {
		const { movementId } = useKnownAvailableParams<{ movementId: string }>();
		const movement = useMovementInfo(movementId, prefetchedData?.movementInfo);

		if (isError(movement)) {
			if (movement.reason === 'notFound') {
				return <Redirect to="/" reason="movement-not-found" />;
			}
			throw new Error(`Failed to load movement details for slugOrId:${movementId}`);
		}

		/**
		 * Show spinner while movement data is loading.
		 */
		if (isLoading(movement)) return <Loader size="m" my="256px" mx="auto" data-testid="loading-spinner" />;

		return (
			<MovementContextProvider movementData={movement.data}>
				<MovementContainer />
			</MovementContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { movementId },
			} = context;
			return {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				movementInfo: await prefetchMovementInfo(movementId!, context),
			};
		},
	},
);
